/* ============================================================
   MOONRISE — <product-recommendations> custom element

   Pattern: Dawn's product-recommendations. Section ships with skeleton
   placeholder cards so the layout is stable; on first viewport intersection
   the element fetches Shopify's recommendation HTML via the
   `routes.product_recommendations_url` endpoint, parses it, and swaps in
   the freshly-rendered grid.

   Why intersection-based, not on connectedCallback:
     - Recommendations sit far below the fold. Deferring the fetch until
       the element scrolls near the viewport keeps initial paint fast and
       avoids burning a request on visitors who never scroll.
   ============================================================ */

(function () {
  'use strict';

  if (customElements.get('product-recommendations')) return;

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      this.url = this.dataset.url;
      this.grid = this.querySelector('[data-recommendations-grid]');
      if (!this.url || !this.grid) return;
      if (this._loaded || this._loading) return;

      if ('IntersectionObserver' in window) {
        var self = this;
        this._io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              self._io.disconnect();
              self._load();
            }
          });
        }, { rootMargin: '0px 0px 400px 0px' });
        this._io.observe(this);
      } else {
        this._load();
      }
    }

    _load() {
      var self = this;
      this._loading = true;
      this.classList.add('is-loading');

      fetch(this.url, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-recommendations-grid]');
          if (!fresh) {
            // API returned no usable HTML — keep skeleton state but flag
            // for graceful styling. Section can still render fallback collection.
            self.classList.add('is-empty');
            return;
          }
          var freshCards = fresh.querySelectorAll('.product-card');
          if (freshCards.length === 0) {
            self.classList.add('is-empty');
            // Hide section entirely when truly empty (no skeleton, no fallback).
            var section = self.closest('.section-related-products');
            if (section) section.setAttribute('hidden', '');
            return;
          }
          self.grid.innerHTML = fresh.innerHTML;
        })
        .catch(function () {
          // Network or parse error — leave skeleton in place silently.
          self.classList.add('is-error');
        })
        .then(function () {
          self._loading = false;
          self._loaded = true;
          self.classList.remove('is-loading');
        });
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
    }
  }

  customElements.define('product-recommendations', ProductRecommendations);
})();
